/**
 * 견적 불러오기(loadEstimate)에서 추출한 순수 데이터 파싱 함수.
 *
 * API 응답을 받아 각 테이블 행 데이터, 체크박스, 등급, 할인율 등을 반환.
 * side-effect 없음 — setState는 호출자(ReactEstimateForm)에서 수행.
 */

import {
  ShasiRow,
  OtherRow,
  TongbaRow,
  AdditionalRow,
  DiscountRates,
  SelectedGrades,
  UnitPricesMap,
  FixedVariablesMap,
  calculateAllShasiPrices,
} from './estimateCalculation';

// ──────────────────────────────────────────────
// 타입 정의
// ──────────────────────────────────────────────

export interface EstimateApiItem {
  productType: string;
  brand?: string;
  item: string;
  installLocation?: string;
  width?: number;
  height?: number;
  quantity?: number;
  price?: number;
  priceWithTax?: number;
  color?: string;
  note?: string;
  directInput?: string;
  estimateNotes?: string;
  windowSpecBrand?: string;
  combinedFrameDoorOptions?: string;
  autoHandleScope?: string;
  specialBenefitType?: string;
}

export interface EstimateApiResponse {
  estimateNumber: string;
  estimateType?: string;
  customerName: string;
  phone: string;
  address: string;
  detailedAddress?: string;
  constructionStatus?: string;
  scheduledDate?: string;
  checkboxes?: string;
  selectedGrades?: string;
  timestamp?: string;
  estimator?: string;
  estimatorUser?: { phoneNumber?: string };
  items?: EstimateApiItem[];
}

export interface CheckboxState {
  homeWindow: boolean;
  lxHausys: boolean;
  kccHomeCC: boolean;
}

export interface EstimateInfoParsed {
  estimateNumber: string;
  estimateDate: string;
  estimator: string;
  estimatorPhone: string;
  estimateType: string;
  customerName: string;
  phone: string;
  address: string;
  detailedAddress: string;
  constructionStatus: string;
  scheduledDate: string;
  estimateNotes: string;
}

export interface ParsedEstimateResult {
  estimateInfo: EstimateInfoParsed;
  checkboxes: CheckboxState;
  selectedGrades: SelectedGrades;
  shasiData: ShasiRow[];
  otherData: OtherRow[];
  tongbaData: TongbaRow[];
  additionalData: AdditionalRow[];
  discountData: { homeWindow: number; 'LX하우시스': number; 'KCC홈CC': number };
  discountRates: DiscountRates;
}

export interface ParseEstimateParams {
  estimateData: EstimateApiResponse;
  itemPrices: { [category: string]: { [itemName: string]: { [installationType: string]: number } } };
  unitPrices: UnitPricesMap;
  fixedVariables: FixedVariablesMap;
  isUnitPricesLoaded: boolean;
}

// ──────────────────────────────────────────────
// 기본 빈 행
// ──────────────────────────────────────────────

const EMPTY_SHASI_ROW: ShasiRow = {
  id: 1, installLocation: '', item: '-', width: '', height: '', quantity: '',
  homeWindowPrice: 0, lxHausysPrice: 0, kccHomeCCPrice: 0, note: '',
};

const EMPTY_OTHER_ROW: OtherRow = {
  id: 1, installLocation: '', item: '-', width: '', height: '', color: '', quantity: '', price: 0, note: '',
};

const EMPTY_TONGBA_ROW: TongbaRow = {
  id: 1, item: '-', spec: '', quantity: '', price: 0,
};

const EMPTY_ADDITIONAL_ROW: AdditionalRow = {
  id: 1, item: '-', quantity: '', price: 0, customItem: '',
};

// ──────────────────────────────────────────────
// 할인율 추출 기준 날짜
// ──────────────────────────────────────────────

const DISCOUNT_RATE_CUTOFF = new Date('2026-01-23T22:00:00');

// ──────────────────────────────────────────────
// 메인 파싱 함수
// ──────────────────────────────────────────────

export function parseEstimateData(params: ParseEstimateParams): ParsedEstimateResult {
  const { estimateData, itemPrices, unitPrices, fixedVariables, isUnitPricesLoaded } = params;

  // ── 기본 정보 ──
  const firstItemNotes = estimateData.items?.[0]?.estimateNotes || '';
  const today = new Date();
  const estimateDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // 원작성자 정보 (API 응답에서 추출)
  const estimator = estimateData.estimator || '견적자';
  const estimatorPhone = estimateData.estimatorUser?.phoneNumber || '1661-4830';

  const estimateInfo: EstimateInfoParsed = {
    estimateNumber: estimateData.estimateNumber,
    estimateDate,
    estimator,
    estimatorPhone,
    estimateType: estimateData.estimateType || '-',
    customerName: estimateData.customerName,
    phone: estimateData.phone,
    address: estimateData.address,
    detailedAddress: estimateData.detailedAddress || '',
    constructionStatus: estimateData.constructionStatus || '시공',
    scheduledDate: estimateData.scheduledDate || '',
    estimateNotes: firstItemNotes,
  };

  // ── 체크박스 복원 ──
  let checkboxes: CheckboxState = { homeWindow: true, lxHausys: true, kccHomeCC: true };
  if (estimateData.checkboxes) {
    try {
      checkboxes = JSON.parse(estimateData.checkboxes);
    } catch {
      // 파싱 실패 시 기본값 유지
    }
  }

  // ── 등급 복원 ──
  let selectedGrades: SelectedGrades = { LX: '프리미엄', 홈윈도우: '프리미엄' };
  if (estimateData.selectedGrades) {
    try {
      const parsed = JSON.parse(estimateData.selectedGrades);
      selectedGrades = {
        LX: parsed.LX || '프리미엄',
        홈윈도우: parsed.홈윈도우 || '프리미엄',
      };
    } catch {
      // 파싱 실패 시 기본값 유지
    }
  }

  // ── 항목 분류 ──
  const shasiGroups: Record<string, ShasiRow> = {};
  const itemCountBySpec: Record<string, number> = {};
  const otherItems: OtherRow[] = [];
  const tongbaItems: TongbaRow[] = [];
  const additionalItems: AdditionalRow[] = [];
  const discountItems: Record<string, number> = {};

  let shasiId = 1;
  let otherId = 1;
  let tongbaId = 1;
  let additionalId = 1;

  estimateData.items?.forEach((item) => {
    switch (item.productType) {
      case '샷시':
      case '샷시&망&랩핑':
        classifyShasiItem(item, shasiGroups, itemCountBySpec, shasiId);
        // shasiId는 shasiGroups에 새 키가 추가될 때만 증가
        shasiId = Object.keys(shasiGroups).length + 1;
        break;

      case '중문 & 기타':
      case '중문&기타':
        otherItems.push({
          id: otherId++,
          installLocation: item.installLocation || '',
          item: item.item,
          width: item.width?.toString() || '',
          height: item.height?.toString() || '',
          color: item.color || '',
          quantity: item.quantity?.toString() || '',
          price: item.price || 0,
          note: item.note || '',
        });
        break;

      case '통바':
        tongbaItems.push({
          id: tongbaId++,
          item: item.item,
          spec: item.width?.toString() || '',
          quantity: item.quantity?.toString() || '',
          price: item.price || 0,
        });
        break;

      case '추가사항':
        additionalItems.push(classifyAdditionalItem(item, additionalId++));
        break;

      case '할인내역':
        if (item.brand) {
          discountItems[item.brand] = item.priceWithTax || 0;
        }
        break;
    }
  });

  // 그룹화된 샷시 → 배열
  const shasiItems = Object.values(shasiGroups);

  // ── 가격 재계산 (price=0인 항목) ──
  const constructionStatus = estimateData.constructionStatus || '시공';
  const recalculatedOtherItems = recalculateOtherPrices(otherItems, itemPrices, constructionStatus);
  const recalculatedTongbaItems = recalculateTongbaPrices(tongbaItems, itemPrices, constructionStatus);
  const recalculatedAdditionalItems = recalculateAdditionalPrices(additionalItems, itemPrices, constructionStatus);

  // ── 할인 데이터 ──
  const discountData = {
    homeWindow: discountItems['HW'] || 0,
    'LX하우시스': discountItems['LX'] || 0,
    'KCC홈CC': discountItems['KC'] || 0,
  };

  // ── 할인율 ──
  const discountRates = extractDiscountRates(estimateData);

  // ── 샷시 가격 재계산 (현재 단가 기준) ──
  const baseShasiData = shasiItems.length > 0 ? shasiItems : [EMPTY_SHASI_ROW];
  let finalShasiData = baseShasiData;
  if (isUnitPricesLoaded && Object.keys(unitPrices).length > 0) {
    finalShasiData = calculateAllShasiPrices(baseShasiData, {
      unitPrices,
      fixedVariables,
      discountRates,
      constructionStatus,
      selectedGrades,
      additionalData: recalculatedAdditionalItems.length > 0 ? recalculatedAdditionalItems : [EMPTY_ADDITIONAL_ROW],
    });
  }

  return {
    estimateInfo,
    checkboxes,
    selectedGrades,
    shasiData: finalShasiData,
    otherData: recalculatedOtherItems.length > 0 ? recalculatedOtherItems : [EMPTY_OTHER_ROW],
    tongbaData: recalculatedTongbaItems.length > 0 ? recalculatedTongbaItems : [EMPTY_TONGBA_ROW],
    additionalData: recalculatedAdditionalItems.length > 0 ? recalculatedAdditionalItems : [EMPTY_ADDITIONAL_ROW],
    discountData,
    discountRates,
  };
}

// ──────────────────────────────────────────────
// 내부 헬퍼 함수
// ──────────────────────────────────────────────

function classifyShasiItem(
  item: EstimateApiItem,
  shasiGroups: Record<string, ShasiRow>,
  itemCountBySpec: Record<string, number>,
  nextId: number
): void {
  // note에서 행 인덱스 추출
  let rowIndex = '';
  let cleanNote = item.note || '';
  if (item.note && item.note.includes('__IDX')) {
    const match = item.note.match(/__IDX(\d+)/);
    if (match) {
      rowIndex = match[1];
      cleanNote = item.note.replace(/__IDX\d+/, '').trim();
    }
  }

  const specKey = `${item.item}_${item.installLocation}_${item.width}_${item.height}_${item.quantity}`;

  let uniqueKey = '';
  if (rowIndex) {
    uniqueKey = `_ROW${rowIndex}`;
  } else {
    if (!itemCountBySpec[specKey]) {
      itemCountBySpec[specKey] = 0;
    }
    if (item.brand === 'HW') {
      itemCountBySpec[specKey]++;
    }
    const currentCount = itemCountBySpec[specKey] || 1;
    uniqueKey = currentCount > 1 ? `_DUP${currentCount}` : '';
  }

  const key = `${specKey}${uniqueKey}`;

  if (!shasiGroups[key]) {
    shasiGroups[key] = {
      id: nextId,
      installLocation: item.installLocation || '',
      item: item.item,
      width: item.width?.toString() || '',
      height: item.height?.toString() || '',
      quantity: item.quantity?.toString() || '',
      homeWindowPrice: 0,
      lxHausysPrice: 0,
      kccHomeCCPrice: 0,
      note: cleanNote,
    };
  }

  switch (item.brand) {
    case 'HW':
      shasiGroups[key].homeWindowPrice = item.price || 0;
      break;
    case 'LX':
      shasiGroups[key].lxHausysPrice = item.price || 0;
      break;
    case 'KC':
      shasiGroups[key].kccHomeCCPrice = item.price || 0;
      break;
  }
}

function classifyAdditionalItem(item: EstimateApiItem, id: number): AdditionalRow {
  let customItem = '';
  if (item.item === '직접입력' || item.item === '직접입력(고객 비공개용)' || item.item === '비공개 금액_선택') {
    customItem = item.directInput || '';
  }

  const price = item.price || 0;
  const isCustomOrPrivate =
    item.item === '직접입력' ||
    item.item === '직접입력(고객 비공개용)' ||
    item.item === '비공개 금액_선택' ||
    item.item === '비공개 금액_주택' ||
    item.item === '비공개 금액_빌라';

  return {
    id,
    item: item.item,
    quantity: item.quantity?.toString() || '',
    price,
    customItem,
    unitPrice: isCustomOrPrivate ? price : 0,
  };
}

function recalculateOtherPrices(
  items: OtherRow[],
  itemPrices: ParseEstimateParams['itemPrices'],
  constructionStatus: string
): OtherRow[] {
  return items.map((row) => {
    if (row.price === 0 && row.item && row.item !== '-' && row.quantity) {
      const quantity = parseFloat(row.quantity) || 0;
      let basePrice = 0;

      if (itemPrices?.['중문&기타']?.[row.item]) {
        basePrice = itemPrices['중문&기타'][row.item][constructionStatus]
          || itemPrices['중문&기타'][row.item]['공통']
          || 0;
      }

      if (basePrice > 0) {
        return { ...row, price: Math.round((basePrice * quantity) / 1000) * 1000 };
      }
    }
    return row;
  });
}

function recalculateTongbaPrices(
  items: TongbaRow[],
  itemPrices: ParseEstimateParams['itemPrices'],
  constructionStatus: string
): TongbaRow[] {
  return items.map((row) => {
    if (row.price === 0 && row.item && row.item !== '-' && row.quantity && row.spec) {
      const quantity = parseFloat(row.quantity) || 0;
      const specValue = parseFloat(row.spec) || 0;
      let basePrice = 0;

      if (itemPrices?.['통바']?.[row.item]) {
        basePrice = itemPrices['통바'][row.item][constructionStatus]
          || itemPrices['통바'][row.item]['공통']
          || 0;
      }

      if (basePrice > 0 && specValue > 0) {
        const multiplier = Math.ceil(specValue / 1000);
        return { ...row, price: basePrice * quantity * multiplier };
      }
    }
    return row;
  });
}

function recalculateAdditionalPrices(
  items: AdditionalRow[],
  itemPrices: ParseEstimateParams['itemPrices'],
  constructionStatus: string
): AdditionalRow[] {
  const skipItems = ['직접입력', '직접입력(고객 비공개용)', '비공개 금액_선택', '비공개 금액_주택', '비공개 금액_빌라'];

  return items.map((row) => {
    if (row.price === 0 && row.item && row.item !== '-' && !skipItems.includes(row.item) && row.quantity) {
      const quantity = parseFloat(row.quantity) || 0;
      let unitPrice = 0;

      if (itemPrices?.['추가사항']?.[row.item]) {
        unitPrice = itemPrices['추가사항'][row.item][constructionStatus]
          || itemPrices['추가사항'][row.item]['공통']
          || 0;
      }

      if (unitPrice > 0) {
        return { ...row, price: unitPrice * quantity };
      }
    }
    return row;
  });
}

function extractDiscountRates(estimateData: EstimateApiResponse): DiscountRates {
  const ratesFromItems: Record<string, string | number> = { HW: '', LX: '', KC: '' };

  estimateData.items?.forEach((item) => {
    if (item.productType === '할인내역' && item.brand) {
      const rateMatch = item.item?.match(/(\d+)% 할인/);
      if (rateMatch) {
        ratesFromItems[item.brand] = parseInt(rateMatch[1]) || 0;
      }
    }
  });

  // 기준 날짜 이전 견적은 할인율 0%
  const estimateTimestamp = estimateData.timestamp ? new Date(estimateData.timestamp) : null;
  const isOldEstimate = estimateTimestamp && estimateTimestamp < DISCOUNT_RATE_CUTOFF;

  if (isOldEstimate) {
    return { homeWindow: '0', 'LX하우시스': '0', 'KCC홈CC': '0' };
  }

  return {
    homeWindow: ratesFromItems['HW'] as string,
    'LX하우시스': ratesFromItems['LX'] as string,
    'KCC홈CC': ratesFromItems['KC'] as string,
  };
}
